import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Group, rowClass } from '../components/field/kit.js'
import { Icon, type IconName } from '../components/Icon.js'
import { Badge, Button, Card, CardHeader } from '../components/ui.js'
import { formatDate } from '../lib/format.js'
import { useGuide, type GuideState } from './GuideProvider.js'

/**
 * Where the tour comes back. Decision 115.
 *
 * Somebody who chose "Don't show this again" turns it back on here — by
 * taking it, or by asking for the welcome again next time — and anybody can
 * reopen the help centre and What's new. Settings → Your account at a desk;
 * Me on a phone.
 */

function tourLine(state: GuideState | null): string {
  if (!state) return 'A minute round the screens you use.'
  if (state.tourCompletedAt) return `You took it on ${formatDate(state.tourCompletedAt)}.`
  if (state.tourDismissedAt) return `Turned off on ${formatDate(state.tourDismissedAt)}. It won’t offer itself until you take it.`
  return 'You haven’t taken it yet.'
}

function useUnseenCount(): number {
  return useGuide().unseen.reduce((sum, release) => sum + release.items.length, 0)
}

export function GuideSettingsCard() {
  const guide = useGuide()
  const unseen = useUnseenCount()
  const offering = Boolean(guide.state && !guide.state.tourCompletedAt && !guide.state.tourDismissedAt)

  return (
    <Card>
      <CardHeader title="Help and the tour" description="Guides for every screen, a tour of the app, and what changed." />
      <ul className="divide-y divide-ink-100">
        <SettingRow
          icon="flag"
          title="The welcome tour"
          detail={tourLine(guide.state)}
          action={
            <div className="flex flex-wrap justify-end gap-1.5">
              {guide.state?.tourDismissedAt ? (
                <Button size="sm" variant="ghost" loading={guide.resetting} onClick={guide.resetTour}>
                  Offer it again
                </Button>
              ) : null}
              <Button size="sm" variant={offering ? 'primary' : 'secondary'} onClick={guide.startTour}>
                {guide.state?.tourCompletedAt ? 'Take the tour again' : 'Take the tour'}
              </Button>
            </div>
          }
        />
        <SettingRow
          icon="book"
          title="Help centre"
          detail="Every guide, searchable. Press ? on any screen for the ones about it."
          action={
            <Link
              to="/help"
              className="inline-flex min-h-[var(--control-sm)] items-center rounded-md bg-white px-[var(--control-pad-sm)] text-xs font-medium text-ink-800 ring-1 ring-ink-300 ring-inset hover:bg-ink-50"
            >
              Open the help centre
            </Link>
          }
        />
        <SettingRow
          icon="news"
          title="What’s new"
          detail={
            unseen > 0
              ? `${unseen === 1 ? 'One change' : `${unseen} changes`} you haven’t seen yet.`
              : 'You’re up to date. Every change is kept on its own page.'
          }
          action={
            unseen > 0 ? (
              <Button size="sm" onClick={guide.openWhatsNew}>
                See what’s new
              </Button>
            ) : (
              <Link
                to="/help/whats-new"
                className="inline-flex min-h-[var(--control-sm)] items-center rounded-md bg-white px-[var(--control-pad-sm)] text-xs font-medium text-ink-800 ring-1 ring-ink-300 ring-inset hover:bg-ink-50"
              >
                All changes
              </Link>
            )
          }
          badge={unseen > 0 ? 'New' : undefined}
        />
      </ul>
    </Card>
  )
}

function SettingRow({
  icon,
  title,
  detail,
  action,
  badge,
}: {
  icon: IconName
  title: string
  detail: string
  action: ReactNode
  badge?: string
}) {
  return (
    <li className="flex flex-wrap items-center gap-3 px-[var(--card-pad)] py-3">
      <span className="grid size-9 shrink-0 place-items-center rounded-md bg-brand-100 text-brand-700">
        <Icon name={icon} />
      </span>
      <div className="min-w-[200px] flex-1">
        <p className="flex items-center gap-2 text-sm font-semibold text-ink-900">
          {title}
          {badge ? <Badge tone="dark">{badge}</Badge> : null}
        </p>
        <p className="text-xs leading-snug text-ink-500">{detail}</p>
      </div>
      {action}
    </li>
  )
}

/** Me's Help group on a phone: the same three, at a thumb's size. */
export function FieldHelpGroup() {
  const guide = useGuide()
  const navigate = useNavigate()
  const unseen = useUnseenCount()

  const row = (icon: IconName, label: string, onClick: () => void, aside?: ReactNode) => (
    <button type="button" onClick={onClick} className={rowClass}>
      <span className="grid size-8 shrink-0 place-items-center rounded-md bg-ink-100 text-ink-700">
        <Icon name={icon} className="size-[18px]" />
      </span>
      <span className="min-w-0 flex-1 text-[15px] font-medium text-ink-900">{label}</span>
      {aside}
      <Icon name="forward" className="size-4 text-ink-400" />
    </button>
  )

  return (
    <div data-guide="field-help">
      <Group label="Help">
        {row('help', 'Help and guides', () => guide.openHelp())}
        {row('bug', 'Report a bug', guide.reportBug)}
        {guide.bugs.mine > 0 || guide.bugs.open > 0
          ? row(
              'jobs',
              'Bug reports',
              () => navigate('/help/bugs'),
              guide.bugs.waitingOnYou > 0 ? (
                <span className="rounded-sm bg-ink-900 px-1.5 py-px text-[11px] font-bold text-white">Your turn</span>
              ) : null,
            )
          : null}
        {row('flag', guide.state?.tourCompletedAt ? 'Take the tour again' : 'Take the tour', guide.startTour)}
        {row(
          'news',
          'What’s new',
          guide.openWhatsNew,
          unseen > 0 ? (
            <span className="rounded-sm bg-ink-900 px-1.5 py-px text-[11px] font-bold text-white tabular-nums">
              {unseen}
            </span>
          ) : null,
        )}
      </Group>
    </div>
  )
}
